"use client";

import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";

import getDeviceType from "@/app/utils/device/getDeviceType";
import { OptionsType, OptionType, type SelectOption } from "@/types";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { ArrowDownIcon, ArrowUpIcon, Cross, UpperVectorIcon } from "../Icons";
import { Modal } from "../modal";

type Props = {
  className: string;
  placeholder?: string;
  dropdownIndicator?: boolean;
  isSearchable?: boolean;
  options: OptionsType;
  formatOptionLabel?: any;
  isClearable?: boolean;
  customComponents?: any;
  onClick?: (op: OptionType) => void;
  closeMenuOnSelect?: boolean;
  loading?: boolean;
  selectedOption?: OptionType;
  setSelectedOption?: Dispatch<SetStateAction<any>>;
  iconComp?: any;
  onInputChange?: (op: string) => void;
  async?: boolean;
  travellerOptions: SelectOption[];
  isCustomClass?: boolean;
};

export default function Dropdown(props: Props) {
  const {
    placeholder,
    options,
    formatOptionLabel,
    travellerOptions,
    onClick,
    selectedOption,
    setSelectedOption,
    iconComp = <></>,
    isCustomClass,
  } = props;
  const t = useTranslations();
  const { resolvedTheme } = useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMobile = getDeviceType().isMobileType;
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (isMobile) {
      setIsModalOpen(true);
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (option: OptionType) => {
    if (onClick) {
      onClick(option);
    }
    if (setSelectedOption) {
      setSelectedOption(option);
    }
    // setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formattedSelected = travellerOptions
    ?.filter((item) => Number(item.value) > 0)
    .map((item) => {
      const count = Number(item.value);
      let label = item.label;

      // Handle singular/plural based on label and count
      if (label === t("HomePageFlights.adults") && count === 1) {
        label = t("HomePageFlights.adult");
      } else if (label === t("HomePageFlights.children") && count === 1) {
        label = t("HomePageFlights.child");
      } else if (label === t("HomePageFlights.infants") && count === 1) {
        label = t("HomePageFlights.infant");
      }

      return `${count} ${label}`;
    })
    .join(", ");

  const sum = travellerOptions?.reduce(
    (sum, item) => sum + Number(item.value),
    0
  );
  const distinctNonZeroCount = travellerOptions?.filter(
    (item) => Number(item.value) > 0
  ).length;

  return (
    <>
      <Modal
        isOpen={isModalOpen && isMobile}
        width="w-full h-screen !rounded-none bg-lightwhite dark:bg-sleap-gray-950 !m-0"
        height="!px-0 !pt-0"
        outerClassName="overflow-y-scroll"
        innerClassName="rounded-2xl p-0"
        bgColor="bg-lightwhite dark:bg-sleap-gray-950"
      >
        {" "}
        <div className="flex justify-between items-center py-4 bg-sleap-gray-950 dark:bg-dark px-4">
          <h4 className="text-white font-bold font-unbounded text-lg mb-0 ">
            {t("Other.travellers")}
          </h4>
          <span
            className="p-2 rounded-full bg-sleap-gray-650 cursor-pointer"
            onClick={() => setIsModalOpen(false)}
          >
            <Cross color={"#fff"} />
          </span>{" "}
        </div>
        <div className="rounded-xl bg-lightwhite dark:bg-dark h-[80vh] flex flex-col px-4">
          <>
            {options.map((option: OptionType, index) => (
              <div
                className="p-2"
                key={index}
                onClick={() => handleSelect(option)}
              >
                {formatOptionLabel(option)}
              </div>
            ))}
          </>
          <div className="dark:border border-sleap-gray-850  dark:bg-dark bg-white px-4 fixed bottom-0 left-0 py-4 z-50 w-full">
            <div className="flex items-center justify-between w-full">
              <button
                className="pointer flex items-center bg-sleap-purple-300 h-12 px-3 py-2 rounded-full gap-x-2 w-full mt-auto "
                onClick={() => setIsModalOpen(false)}
              >
                <h5 className="text-base font-semibold font-switzer text-white mb-0">
                  {t("Other.confirm")}
                </h5>
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <div ref={dropdownRef} className="sm:relative sm:0">
        <div
          className={`traveler-input-container md:w-full h-12 flex flex-row justify-between rounded-full gap-0.5 px-6 border-[1px] 2xl:border-2 gap-x-2 items-center ${isCustomClass ? "bg-transparent text-lightwhite hover:border-sleap-gray-50" : "bg-lightwhite text-sleap-gray-950 dark:bg-sleap-gray-950 dark:text-lightwhite hover:border-sleap-gray-600"} `}
          onClick={handleToggle}
        >
          {iconComp}{" "}
          <div
            className={`sm:mx-0 mx-auto text-center w-11/12 overflow-x-auto whitespace-nowrap relative `}
          >
            <pre className="mb-0 font-medium font-inter text-base text-left">
              {selectedOption?.value ? formattedSelected : placeholder}
            </pre>
          </div>
          <span className="w-6 pe-2">
            {isOpen ? (
              <ArrowUpIcon
                color={
                  resolvedTheme === "dark"
                    ? "offWhite"
                    : isCustomClass
                      ? "white"
                      : "lightblack"
                }
              />
            ) : (
              <Fragment>
                <span className="dark:hidden block">
                  <ArrowDownIcon
                    color={isCustomClass ? "white" : "lightblack"}
                  />
                </span>
                <span className="dark:block hidden">
                  <ArrowDownIcon color={"offWhite"} />
                </span>
              </Fragment>
            )}
          </span>
        </div>
        {isOpen && !isMobile && (
          <div
            className={`absolute right-0 z-20 w-full sm:p-0.5 sm:pe-[2.25px] sm:rounded-xl sm:h-fit h-full`}
          >
            <div className="rounded-xl border border-sleap-gray-950 dark:bg-sleap-gray-950 bg-white">
              {options.map((option: OptionType, index) => (
                <div key={index} onClick={() => handleSelect(option)}>
                  {formatOptionLabel(option)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
